// A tiny shared "navigation lock" (Session SC7A review). A page that is in the middle of something that leaving would
// spoil (the Release and send run, which holds per-row results on screen and a half-finished batch) holds the lock while
// it is busy; the hub tab strip and the menu links then render inert (aria-disabled, no navigation) until it lets go.
//
// A lock is a token held by one page. useNavigationLock(locked) takes it while `locked` is true and ALWAYS lets go on
// unmount, so a page that is closed some other way (the browser's back button, a sign-out) can never leave the menu dead.
import { useEffect, useRef, useSyncExternalStore, type KeyboardEvent, type MouseEvent } from "react";

const held = new Set<symbol>();
const listeners = new Set<() => void>();
let snapshot = false;

function emit() {
  const next = held.size > 0;
  if (next === snapshot) return;
  snapshot = next;
  for (const l of [...listeners]) l();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** True while any page holds the lock. */
export function useIsNavigationLocked(): boolean {
  return useSyncExternalStore(subscribe, () => snapshot, () => false);
}

/** Hold the lock while `locked` is true; released when it turns false and on unmount. */
export function useNavigationLock(locked: boolean): void {
  const token = useRef<symbol>(Symbol("navigation-lock"));
  useEffect(() => {
    const t = token.current;
    if (locked) held.add(t);
    else held.delete(t);
    emit();
    return () => {
      held.delete(t);
      emit();
    };
  }, [locked]);
}

/** Props that make a link inert while locked: announced as disabled, a click or Enter goes nowhere. */
export function lockedLinkProps(locked: boolean): { "aria-disabled"?: true; onClick?: (e: MouseEvent) => void; tabIndex?: number } {
  if (!locked) return {};
  return {
    "aria-disabled": true,
    onClick: (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    },
  };
}

/** Swallow arrow, Home and End keys that would move between tabs while locked. */
export function ignoreNavKeysWhenLocked(locked: boolean, e: KeyboardEvent): boolean {
  if (!locked) return false;
  if (["ArrowRight", "ArrowLeft", "Home", "End"].includes(e.key)) {
    e.preventDefault();
    return true;
  }
  return false;
}

/** For tests: drop every lock. */
export function resetNavigationLockForTests(): void {
  held.clear();
  emit();
}
